import React, { useCallback, useMemo, useState } from 'react';
import { BaseTextInputProps } from '../shared-types';
import { getGetIdFn } from '../utils';
import BaseInputIcon from '../BaseInput/BaseInputIcon';
import TextInput from '../TextInput/TextInput';
import { validateSSNInput } from '../SSNInput/SSNInput';

export type Props = BaseTextInputProps & {
  componentName?: string;
  initialValue?: string;
};

export function parseSSNInput(
  input: string,
  // Whether the '*' character should be allowed as input
  allowMaskingCharacter = false
): string {
  const replaceRegex = allowMaskingCharacter ? /[^0-9•*]/g : /\D/g;
  return input.replace(replaceRegex, '').substring(0, 9);
}

export function getSSNDisplayValue(ssn: string): string {
  const parts = [];

  let i = 0;
  parts.push(ssn.substring(i, i + 3));
  if (ssn.length > 3) {
    i += 3;
    parts.push(ssn.substring(i, i + 2));
    if (ssn.length > 5) {
      i += 2;
      parts.push(ssn.substring(i, i + 4));
    }
  }

  return parts
    .map((part) =>
      part
        .split('')
        .map((char) => {
          if (char === '*' || char === '•') return '•';
          return char;
        })
        .join('')
    )
    .join('-');
}

/**
 * A version of <SSNInput> with the primary purpose of rendering a partially masked SSN value (e.g. •••-••-1234).
 * Note that this version doesn't support showing/hiding the user inputted digits.
 */
const SSNInput2: React.FC<Props> = ({
  onChange,
  disabled,
  error,
  label,
  showRequiredAsterisk,
  'aria-label': ariaLabel,
  readOnly,
  hint,
  onHintToggle,
  componentName = 'SSNInput',
  initialValue = '',
  'data-testid': dataTestID,
  eventId,
  ...inputProps
}: Props) => {
  const id = useMemo(
    () => getGetIdFn(componentName)(label),
    [componentName, label]
  );

  const [value, setValue] = useState(parseSSNInput(initialValue || '', true)); // stripped out dashes, "raw" value
  const valueToDisplay = useMemo(() => getSSNDisplayValue(value), [value]);

  const validInputIcon = useMemo(
    () =>
      validateSSNInput(value) ? (
        <BaseInputIcon
          aria-label="Valid"
          icon="checkmarkCircleInverted"
          color={{
            type: 'theme',
            colorLegacy: 'Green/50',
            colorNext: 'success/default',
          }}
        />
      ) : null,
    [value]
  );

  const handleChange = useCallback(
    (v: string) => {
      const newValue = parseSSNInput(v);
      setValue(newValue);
      if (onChange) {
        onChange(newValue);
      }
    },
    [onChange, setValue]
  );

  return (
    <TextInput
      {...inputProps}
      label={label}
      showRequiredAsterisk={showRequiredAsterisk}
      data-testid={dataTestID || id}
      error={error}
      eventId={eventId}
      readOnly={readOnly}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={handleChange}
      value={valueToDisplay}
      hint={hint}
      onHintToggle={onHintToggle}
      placeholder="123-45-6789"
      adornmentLeft={
        <BaseInputIcon
          icon="lock2"
          size={16}
          color={{
            type: 'theme',
            colorLegacy: 'Neutral/90',
            colorNext: 'neutral/default',
          }}
          aria-hidden
        />
      }
      adornmentRight={validInputIcon}
    />
  );
};

export default SSNInput2;
